import type { Editor } from '@tiptap/core'
import { TextSelection, type Transaction } from '@tiptap/pm/state'

import { updateToolbarContextNodePos } from '../toolbar/contextRegistry'
import { closeMediaToolbar, openMediaToolbar } from '../toolbar/mount'
import {
  findGripperForMedia,
  findMediaTarget,
  getMediaNodeType,
  hasFinePointer,
  isInsideMediaControlsUI,
  isInteractiveEmbed,
  resolveMediaFromGripper
} from './media-target'

/** Grace period to traverse from the inline bar to the detached `…`/submenu popovers. */
const HOVER_HIDE_DELAY_MS = 250

interface MediaControlsState {
  activeTarget: HTMLElement | null
  activeNodePos: number | null
  selectionLocked: boolean
  mediaResizing: boolean
  listenersAttached: boolean
  hoverHideTimer: ReturnType<typeof setTimeout> | null
  pointerX: number
  pointerY: number
  pointerMoveAttached: boolean
  onPointerOutside: (event: PointerEvent) => void
  onDocumentKeyDown: (event: KeyboardEvent) => void
  onWindowResize: () => void
  onPointerMove: (event: PointerEvent) => void
}

const controlsByEditor = new WeakMap<Editor, MediaControlsState>()

function createControlsState(editor: Editor): MediaControlsState {
  return {
    activeTarget: null,
    activeNodePos: null,
    selectionLocked: false,
    mediaResizing: false,
    listenersAttached: false,
    hoverHideTimer: null,
    pointerX: 0,
    pointerY: 0,
    pointerMoveAttached: false,
    onPointerOutside: (event) => handlePointerOutside(editor, event),
    onDocumentKeyDown: (event) => {
      handleMediaDeleteKey(editor, event)
      handleToolbarEscape(editor, event)
    },
    onWindowResize: () => hideMediaResizeControls(editor),
    onPointerMove: (event) => {
      const state = getControlsState(editor)
      state.pointerX = event.clientX
      state.pointerY = event.clientY
    }
  }
}

function getControlsState(editor: Editor): MediaControlsState {
  let state = controlsByEditor.get(editor)
  if (!state) {
    state = createControlsState(editor)
    controlsByEditor.set(editor, state)
  }
  return state
}

function clearActiveState(state: MediaControlsState): void {
  state.activeTarget = null
  state.activeNodePos = null
  state.selectionLocked = false
  state.mediaResizing = false
}

function canUseHoverControls(state: MediaControlsState, finePointer: boolean): boolean {
  return finePointer && !state.selectionLocked
}

function isDeleteKey(event: KeyboardEvent): boolean {
  return event.key === 'Delete' || event.key === 'Backspace'
}

function isFormControlInMediaUI(element: Element | null): boolean {
  if (!(element instanceof HTMLElement) || !isInsideMediaControlsUI(element)) {
    return false
  }
  const tag = element.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/** The caption is an editable shell in node content; its Backspace/Delete must edit text, not nuke the node. */
function isEditingMediaCaption(element: Element | null): boolean {
  return element instanceof HTMLElement && element.closest('.hm-caption') != null
}

/** Set by the drag layer so hover-out/outside-pointer don't tear down controls mid-drag. */
export function setMediaResizing(editor: Editor, active: boolean): void {
  getControlsState(editor).mediaResizing = active
}

/** The pixel-sized loading-shell host, not the wrapper-with-caption, is the visible media box. */
function resolveMediaBox(target: HTMLElement): HTMLElement {
  const host = target.querySelector('.hm-media-host')
  return host instanceof HTMLElement ? host : target
}

/**
 * Overlay the visible media box. Both rects come from `getBoundingClientRect`, then convert
 * into the gripper's own `offsetParent` frame (its border via clientLeft/Top). Drag math
 * reads and writes `offset*`, so the conversion keeps it in one coordinate space.
 */
function positionResizeGripper(gripper: HTMLElement, target: HTMLElement): void {
  const parent = gripper.offsetParent as HTMLElement | null
  const parentRect = parent?.getBoundingClientRect()
  const box = resolveMediaBox(target).getBoundingClientRect()

  const originLeft = (parentRect?.left ?? 0) + (parent?.clientLeft ?? 0)
  const originTop = (parentRect?.top ?? 0) + (parent?.clientTop ?? 0)

  gripper.style.width = `${box.width}px`
  gripper.style.height = `${box.height}px`
  gripper.style.left = `${box.left - originLeft}px`
  gripper.style.top = `${box.top - originTop}px`
}

function activateResizeGripper(gripper: HTMLElement, target: HTMLElement): void {
  // `--active` flips display:none→block first: a hidden gripper has a null offsetParent
  // and zero-size rects, so positionResizeGripper must run while it is laid out.
  gripper.classList.add('hypermultimedia__resize-gripper--active')
  positionResizeGripper(gripper, target)
}

function deactivateResizeGripper(gripper: HTMLElement): void {
  gripper.classList.remove(
    'hypermultimedia__resize-gripper--active',
    'hypermultimedia__resize-gripper--dragging'
  )
  gripper.style.width = ''
  gripper.style.height = ''
  gripper.style.left = ''
  gripper.style.top = ''
}

/** Active grippers whose media sibling is gone (delete, cut, collab) must not linger in the DOM. */
function purgeOrphanedResizeControls(root: ParentNode = document): void {
  root.querySelectorAll('.hypermultimedia__resize-gripper--active').forEach((node) => {
    if (!(node instanceof HTMLElement)) return
    const media = resolveMediaFromGripper(node, root)
    if (!media || !findMediaTarget(media, root)) {
      deactivateResizeGripper(node)
    }
  })
}

function cancelHoverHide(state: MediaControlsState): void {
  if (state.hoverHideTimer != null) {
    window.clearTimeout(state.hoverHideTimer)
    state.hoverHideTimer = null
  }
}

function isPointerOverActiveControls(editor: Editor): boolean {
  const state = getControlsState(editor)
  if (!state.activeTarget) return false

  // Keyboard use parks the pointer anywhere: typing in the Replace dialog or
  // arrowing through menus must not die to the pointer-based 250ms hide.
  const focused = document.activeElement
  if (focused instanceof HTMLElement && isInsideMediaControlsUI(focused)) return true

  const hit = document.elementFromPoint(state.pointerX, state.pointerY)
  if (!(hit instanceof HTMLElement)) return false
  if (isInsideMediaControlsUI(hit)) return true
  return findMediaTarget(hit, editor.view.dom) === state.activeTarget
}

function scheduleHoverHide(editor: Editor, event?: MouseEvent): void {
  const state = getControlsState(editor)
  if (event) {
    state.pointerX = event.clientX
    state.pointerY = event.clientY
  }

  cancelHoverHide(state)
  state.hoverHideTimer = window.setTimeout(() => {
    state.hoverHideTimer = null
    if (!isPointerOverActiveControls(editor)) hideMediaResizeControls(editor)
  }, HOVER_HIDE_DELAY_MS)
}

function setPointerTracking(editor: Editor, state: MediaControlsState, attach: boolean): void {
  if (state.pointerMoveAttached === attach) return
  if (attach) {
    document.addEventListener('pointermove', state.onPointerMove, { passive: true })
  } else {
    document.removeEventListener('pointermove', state.onPointerMove)
  }
  state.pointerMoveAttached = attach
}

export function hideMediaResizeControls(editor: Editor): void {
  const state = getControlsState(editor)
  const gripper = state.activeTarget
    ? findGripperForMedia(state.activeTarget, editor.view.dom)
    : null

  cancelHoverHide(state)
  setPointerTracking(editor, state, false)
  if (gripper) deactivateResizeGripper(gripper)
  // Idle controls have no active target, and `closeMediaToolbar` then falls back
  // to `document` — which would tear down another editor's mounted bar.
  closeMediaToolbar(state.activeTarget ?? editor.view.dom)
  clearActiveState(state)
  setOutsideListeners(state, false)
  purgeOrphanedResizeControls(editor.view.dom)
}

/** Keeps Delete-key and toolbar actions aimed at the node when edits above shift its position. */
export function remapActiveNodePos(editor: Editor, transactions: readonly Transaction[]): void {
  const state = getControlsState(editor)
  if (state.activeNodePos === null || state.mediaResizing) return

  let pos = state.activeNodePos
  for (const tr of transactions) {
    if (!tr.docChanged) continue
    const mapped = tr.mapping.mapResult(pos)
    if (mapped.deleted) {
      hideMediaResizeControls(editor)
      return
    }
    pos = mapped.pos
  }
  if (pos === state.activeNodePos) return

  state.activeNodePos = pos
  if (state.activeTarget) updateToolbarContextNodePos(state.activeTarget, pos)
}

/** Runs post-DOM-update: edits above reflow the node, but the gripper's inline box is a snapshot. */
function repositionActiveGripper(editor: Editor, state: MediaControlsState): void {
  if (!state.activeTarget) return
  const gripper = findGripperForMedia(state.activeTarget, editor.view.dom)
  if (gripper?.classList.contains('hypermultimedia__resize-gripper--active')) {
    positionResizeGripper(gripper, state.activeTarget)
  }
}

/** Tear down when the node behind the active controls has left the document (delete, cut, remote edit). */
export function syncControlsToDoc(editor: Editor): void {
  const state = getControlsState(editor)
  if (state.mediaResizing) return
  // Idle controls ⇒ nothing to tear down; skip the document-wide orphan scan on every transaction.
  if (!state.activeTarget && !state.listenersAttached) return
  if (state.activeTarget && !state.activeTarget.isConnected) {
    hideMediaResizeControls(editor)
    return
  }
  repositionActiveGripper(editor, state)
  purgeOrphanedResizeControls(editor.view.dom)
}

// Scroll is not a teardown trigger: gripper rides content; toolbar tracks via autoUpdate.
function setOutsideListeners(state: MediaControlsState, attach: boolean): void {
  if (state.listenersAttached === attach) return

  if (attach) {
    document.addEventListener('pointerdown', state.onPointerOutside, true)
    document.addEventListener('keydown', state.onDocumentKeyDown, true)
    window.addEventListener('resize', state.onWindowResize)
  } else {
    document.removeEventListener('pointerdown', state.onPointerOutside, true)
    document.removeEventListener('keydown', state.onDocumentKeyDown, true)
    window.removeEventListener('resize', state.onWindowResize)
  }

  state.listenersAttached = attach
}

function handlePointerOutside(editor: Editor, event: PointerEvent): void {
  const state = getControlsState(editor)
  if (state.mediaResizing) return
  const clickTarget = event.target as HTMLElement | null
  if (isInsideMediaControlsUI(clickTarget)) return
  const media = findMediaTarget(clickTarget, editor.view.dom)
  if (media === state.activeTarget) return
  hideMediaResizeControls(editor)
}

/** DOM → doc position with a node-type guard; also re-resolves stale drag-end positions. */
export function resolveMediaNodePos(
  view: Editor['view'],
  target: HTMLElement,
  nodeType: string
): number | null {
  try {
    const pos = view.posAtDOM(target, 0)
    const node = view.state.doc.nodeAt(pos)
    if (node?.type.name === nodeType) return pos

    const $pos = view.state.doc.resolve(pos)
    if ($pos.nodeAfter?.type.name === nodeType) return $pos.pos
    if ($pos.nodeBefore?.type.name === nodeType) {
      return $pos.pos - $pos.nodeBefore.nodeSize
    }
  } catch {
    return null
  }
  return null
}

/** Desktop hover or touch click — gripper + toolbar, or toolbar-only for X embeds. */
function showMediaResizeControls(editor: Editor, target: HTMLElement): boolean {
  const state = getControlsState(editor)
  const nodeType = getMediaNodeType(target)
  if (!nodeType) return false

  const nodePos = resolveMediaNodePos(editor.view, target, nodeType)
  if (nodePos === null) return false

  const gripper = findGripperForMedia(target, editor.view.dom)
  const toolbarOnly = nodeType === 'x'
  if (!gripper && !toolbarOnly) return false

  if (state.activeTarget && state.activeTarget !== target) {
    hideMediaResizeControls(editor)
  }

  cancelHoverHide(state)

  if (gripper) activateResizeGripper(gripper, target)
  openMediaToolbar({
    target,
    editor,
    nodeType,
    nodePos
  })

  state.activeTarget = target
  state.activeNodePos = nodePos
  setPointerTracking(editor, state, true)
  setOutsideListeners(state, true)
  return true
}

function lockMediaSelection(editor: Editor): void {
  getControlsState(editor).selectionLocked = true
}

function deleteActiveMediaNode(editor: Editor): boolean {
  const state = getControlsState(editor)
  const { activeTarget, activeNodePos, mediaResizing } = state
  if (!activeTarget || activeNodePos === null || mediaResizing || !editor.isEditable) {
    return false
  }

  const node = editor.state.doc.nodeAt(activeNodePos)
  const nodeType = getMediaNodeType(activeTarget)
  if (!node || !nodeType || node.type.name !== nodeType) return false

  editor.view.dispatch(editor.state.tr.delete(activeNodePos, activeNodePos + node.nodeSize))
  return true
}

function handleMediaDeleteKey(editor: Editor, event: KeyboardEvent): boolean {
  if (!isDeleteKey(event)) return false
  if (event.metaKey || event.ctrlKey || event.altKey || event.isComposing) return false
  if (!getControlsState(editor).activeTarget) return false
  if (isFormControlInMediaUI(document.activeElement)) return false
  if (isEditingMediaCaption(document.activeElement)) return false
  // A focused caret in text must keep editing text. Hover controls only own the
  // key when the node is selected or focus is outside the editor.
  if (editor.view.hasFocus() && editor.state.selection instanceof TextSelection) return false
  if (!deleteActiveMediaNode(editor)) return false

  event.preventDefault()
  return true
}

/** Escape with focus in the toolbar dismisses it and refocuses the editor. Skips when focus sits in a floating popover — its own bubble-phase Escape closes just the popover. */
function handleToolbarEscape(editor: Editor, event: KeyboardEvent): boolean {
  if (event.key !== 'Escape' || !getControlsState(editor).activeTarget) return false
  const focused = document.activeElement
  if (!(focused instanceof HTMLElement) || focused.closest('.floating-popover')) return false
  if (!focused.closest('[data-hm-toolbar]')) return false

  hideMediaResizeControls(editor)
  editor.commands.focus()
  event.preventDefault()
  return true
}

export function handleMediaHover(editor: Editor, event: MouseEvent, finePointer: boolean): boolean {
  const state = getControlsState(editor)
  if (!canUseHoverControls(state, finePointer)) return false

  cancelHoverHide(state)
  state.pointerX = event.clientX
  state.pointerY = event.clientY

  const fromTarget = event.target as HTMLElement
  if (isInsideMediaControlsUI(fromTarget)) return false

  const target = findMediaTarget(fromTarget, editor.view.dom)
  if (!target || target === state.activeTarget) return false

  showMediaResizeControls(editor, target)
  return false
}

export function handleMediaUnhover(
  editor: Editor,
  event: MouseEvent,
  finePointer: boolean
): boolean {
  const state = getControlsState(editor)
  if (!canUseHoverControls(state, finePointer) || !state.activeTarget || state.mediaResizing) {
    return false
  }

  const related = event.relatedTarget as HTMLElement | null
  if (isInsideMediaControlsUI(related)) return false
  if (findMediaTarget(related, editor.view.dom) === state.activeTarget) return false

  scheduleHoverHide(editor, event)
  return false
}

export function handleMediaClick(editor: Editor, event: MouseEvent | TouchEvent): boolean {
  if (isInsideMediaControlsUI(event.target as HTMLElement)) return false

  const target = findMediaTarget(event.target as HTMLElement, editor.view.dom)
  if (!target) return false

  // Notion-style: embed iframes stay interactive; only images use click-to-lock.
  if (isInteractiveEmbed(getMediaNodeType(target))) return false

  lockMediaSelection(editor)
  showMediaResizeControls(editor, target)
  event.preventDefault()
  return true
}

/** Re-mount hover controls after resize rebuilds the gripper decoration. */
export function restoreControlsAfterResize(
  editor: Editor,
  gripper: HTMLElement,
  nodePos?: number
): void {
  setMediaResizing(editor, false)

  let target: HTMLElement | null = null
  if (nodePos !== undefined) {
    target = editor.view.nodeDOM(nodePos) as HTMLElement | null
  } else {
    target = resolveMediaFromGripper(gripper, editor.view.dom)
  }

  hideMediaResizeControls(editor)

  if (hasFinePointer() && target?.matches(':hover')) {
    showMediaResizeControls(editor, target)
  }

  editor.commands.focus()
}
